/**
 * Slice 57 — owner photo groups (per room) for the Walk shot list.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/queryKeys";
import type { PhotoGroup } from "@/lib/photoGroups";

const key = (propertyId: string) => ["photo-groups", propertyId] as const;

// The generated Supabase types predate this table, so go through an untyped handle.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const table = () => (supabase as unknown as { from: (t: string) => any }).from("property_photo_groups");

export function usePhotoGroups(propertyId: string | undefined) {
  return useQuery({
    queryKey: key(propertyId ?? ""),
    queryFn: async (): Promise<PhotoGroup[]> => {
      if (!propertyId) return [];
      const { data, error } = await table()
        .select("*")
        .eq("property_id", propertyId)
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: true });
      // Before the migration is run the table does not exist: behave as "no groups yet".
      if (error) {
        if (/relation .* does not exist|schema cache/i.test(error.message ?? "")) return [];
        throw error;
      }
      return (data ?? []) as PhotoGroup[];
    },
    enabled: !!propertyId,
  });
}

export function useCreatePhotoGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      propertyId: string;
      roomId: string;
      name: string;
      prompt: string;
      sortOrder: number;
      referencePhotoUrl?: string | null;
    }) => {
      const { data, error } = await table()
        .insert({
          property_id: input.propertyId,
          room_id: input.roomId,
          name: input.name,
          prompt: input.prompt,
          sort_order: input.sortOrder,
          ...(input.referencePhotoUrl ? { reference_photo_url: input.referencePhotoUrl } : {}),
        })
        .select()
        .single();
      if (error) throw error;
      return data as PhotoGroup;
    },
    onSuccess: (row) => qc.invalidateQueries({ queryKey: key(row.property_id) }),
  });
}

export function useUpdatePhotoGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      propertyId: string;
      name?: string;
      prompt?: string;
      referencePhotoUrl?: string | null;
      sortOrder?: number;
    }) => {
      const patch: Record<string, unknown> = {};
      if (input.name !== undefined) patch.name = input.name;
      if (input.prompt !== undefined) patch.prompt = input.prompt;
      if (input.referencePhotoUrl !== undefined) patch.reference_photo_url = input.referencePhotoUrl;
      if (input.sortOrder !== undefined) patch.sort_order = input.sortOrder;
      const { error } = await table().update(patch).eq("id", input.id);
      if (error) throw error;
      return input;
    },
    onSuccess: (v) => qc.invalidateQueries({ queryKey: key(v.propertyId) }),
  });
}

export function useDeletePhotoGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; propertyId: string }) => {
      const { error } = await table().delete().eq("id", input.id);
      if (error) throw error;
      return input;
    },
    onSuccess: (v) => {
      qc.invalidateQueries({ queryKey: key(v.propertyId) });
      qc.invalidateQueries({ queryKey: queryKeys.unitItems.byProperty(v.propertyId) });
    },
  });
}
